import { Component, computed, DestroyRef, effect, inject, input, model, signal } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { FormsModule } from '@angular/forms';
import { of } from 'rxjs';

import { MessageService } from '@openng/optimus-ui/api';
import { ButtonModule } from '@openng/optimus-ui/button';
import { DialogModule } from '@openng/optimus-ui/dialog';
import { IconFieldModule } from '@openng/optimus-ui/iconfield';
import { InputIconModule } from '@openng/optimus-ui/inputicon';
import { InputTextModule } from '@openng/optimus-ui/inputtext';
import { TableModule } from '@openng/optimus-ui/table';
import { TagModule } from '@openng/optimus-ui/tag';

import { CurrencyFormatPipe, DropdownData, GenericDropdownComponent, rxResourceFixed } from '@zayan-erp/shared';
import {
  DsmPackageCopyPreviewResult,
  DsmPackageCopyPreviewTarget,
  DsmPackageCopyStatus,
  DsmPackageCopyTargetResult,
  DsmPackageDetail,
  DsmPackageProductSummary,
  DsmPackageProjectModelElevationOption
} from '../../models/dsm-package.models';
import { DsmPackageService } from '../../services/dsm-package.service';

const NO_VALUE = '—';

type RowState = 'ok' | 'partial' | 'refused' | 'checking' | 'failed' | null;

interface ElevationRow {
  elevationId: string;
  modelId: string;
  label: string;
  matchesSearch: boolean;
  isSource: boolean;
  selectable: boolean;
  selected: boolean;
  state: RowState;
  reason: string | null;
  missingOptionsTooltip: string | null;
}

interface ModelRow {
  modelId: string;
  modelName: string;
  expanded: boolean;
  visible: boolean;
  elevations: ElevationRow[];
  visibleElevations: ElevationRow[];
  selectableCount: number;
  selectedCount: number;
  allSelected: boolean;
}

interface CopyOutcome {
  label: string;
  status: 'created' | 'skipped' | 'refused';
  reason: string | null;
}

@Component({
  selector: 'dsm-option-package-copy',
  templateUrl: './dsm-option-package-copy.component.html',
  imports: [
    FormsModule,
    ButtonModule,
    DialogModule,
    GenericDropdownComponent,
    IconFieldModule,
    InputIconModule,
    InputTextModule,
    TableModule,
    TagModule,
    CurrencyFormatPipe
  ]
})
export class DsmOptionPackageCopyComponent {
  readonly #packageService = inject(DsmPackageService);
  readonly #messageService = inject(MessageService);
  readonly #destroyRef = inject(DestroyRef);

  readonly sourcePackage = input.required<DsmPackageDetail>();

  readonly visible = model<boolean>(false);

  readonly searchTerm = signal('');
  readonly #normalizedSearch = computed(() => this.searchTerm().trim().toLowerCase());

  readonly #expandOverrides = signal<Map<string, boolean>>(new Map());
  readonly #selectedIds = signal<Set<string>>(new Set());

  readonly isCopying = signal(false);
  readonly copyError = signal<string | null>(null);

  readonly outcomes = signal<CopyOutcome[] | null>(null);

  readonly #packageId = computed(() => this.sourcePackage().id);
  readonly #projectId = computed(() => this.sourcePackage().project?.id ?? null);

  readonly #modelElevationsResource = rxResourceFixed({
    params: () => ({ projectId: this.visible() ? this.#projectId() : null }),
    defaultValue: [] as DsmPackageProjectModelElevationOption[],
    stream: ({ params }) =>
      params.projectId
        ? this.#packageService.getProjectModelElevations(params.projectId)
        : of([] as DsmPackageProjectModelElevationOption[])
  });

  readonly isLoadingTargets = computed(() => this.#modelElevationsResource.isLoading());
  readonly targetsLoadFailed = computed(() => !!this.#modelElevationsResource.error());
  readonly hasAnyTargets = computed(() =>
    this.#modelElevationsResource.value().some(pair => !this.#isSourcePair(pair))
  );

  readonly #modelGroups = computed(() => {
    const byModel = new Map<string, { modelName: string; elevations: DsmPackageProjectModelElevationOption[] }>();

    this.#modelElevationsResource.value().forEach(pair => {
      const group = byModel.get(pair.modelId) ?? { modelName: pair.modelName || NO_VALUE, elevations: [] };
      group.elevations.push(pair);
      byModel.set(pair.modelId, group);
    });

    return [...byModel].sort(([, a], [, b]) => a.modelName.localeCompare(b.modelName));
  });

  /** Every candidate target — what the batch preview checks, up front, regardless of
   *  what's actually selected. */
  readonly #candidateTargets = computed(() =>
    this.#modelElevationsResource
      .value()
      .filter(pair => !this.#isSourcePair(pair))
      .map(pair => ({ modelId: pair.modelId, elevationId: pair.elevationId }))
  );

  /** One batch call answers for every candidate target at once: which can take a copy at
   *  all, and — per target — which of the source package's options are configured there.
   *  Fires as soon as the tree has targets to check, so ineligible ones come up disabled
   *  in the picker immediately rather than only after being selected. */
  readonly #previewResource = rxResourceFixed({
    params: () => ({
      packageId: this.#packageId(),
      targets: this.visible() ? this.#candidateTargets() : []
    }),
    defaultValue: null as DsmPackageCopyPreviewResult | null,
    stream: ({ params }) =>
      params.packageId && params.targets.length
        ? this.#packageService.previewCopyPackage(params.packageId, params.targets)
        : of(null)
  });

  readonly isCheckingTargets = computed(() => this.#previewResource.isLoading() && this.#candidateTargets().length > 0);
  readonly previewFailed = computed(() => !!this.#previewResource.error());

  readonly #previewByElevationId = computed(() => {
    const map = new Map<string, DsmPackageCopyPreviewTarget>();
    (this.#previewResource.value()?.targets ?? []).forEach(target => map.set(target.elevationId, target));

    return map;
  });

  readonly #totalSourceOptions = computed(() => this.sourcePackage().products?.length ?? 0);

  /** Every model in the project, each with its elevations — the full "Apply To" tree. */
  readonly #allModelRows = computed<ModelRow[]>(() => {
    const source = this.sourcePackage();
    const term = this.#normalizedSearch();
    const overrides = this.#expandOverrides();
    const selected = this.#selectedIds();
    const preview = this.#previewByElevationId();
    const checking = this.isCheckingTargets();
    const failed = this.previewFailed();
    const totalOptions = this.#totalSourceOptions();
    const groups = this.#modelGroups();

    return groups.map(([modelId, group]) =>
      this.#buildModelRow(
        modelId,
        group.modelName,
        group.elevations,
        source,
        term,
        overrides,
        selected,
        preview,
        checking,
        failed,
        totalOptions,
        groups.length
      )
    );
  });

  /** What the search box narrows the tree down to. */
  readonly models = computed(() => this.#allModelRows().filter(model => model.visible));

  /** True once the resource resolved but the project has no other models/elevations at all. */
  readonly noModelsInProject = computed(
    () => !this.isLoadingTargets() && !this.targetsLoadFailed() && !this.hasAnyTargets()
  );

  /** True once the tree has models, but the current search matches none of them. */
  readonly noSearchMatches = computed(
    () => !this.isLoadingTargets() && !this.targetsLoadFailed() && this.hasAnyTargets() && this.models().length === 0
  );

  readonly #allSelectableIds = computed(() =>
    this.#allModelRows().flatMap(model =>
      model.elevations.filter(elevation => elevation.selectable).map(elevation => elevation.elevationId)
    )
  );

  readonly allSelected = computed(() => {
    const all = this.#allSelectableIds();

    return all.length > 0 && all.every(id => this.#selectedIds().has(id));
  });

  readonly selectAllDisabled = computed(() => this.#allSelectableIds().length === 0);

  readonly selectedCount = computed(() => this.#selectedIds().size);

  readonly copyButtonLabel = computed(() =>
    this.selectedCount() > 1 ? `Create ${this.selectedCount()} Copies` : 'Create Copy'
  );

  readonly canCopy = computed(() => this.selectedCount() > 0 && !this.isCopying());

  readonly copySourceSummary = computed(() => {
    const source = this.sourcePackage();
    const modelName = source.modelName || NO_VALUE;
    const elevation = source.elevationCode || NO_VALUE;

    return `${modelName} / Elevation ${elevation}`;
  });

  readonly projectSelectOptions = computed<DropdownData[]>(() => [
    { id: 'project', firstLabel: this.sourcePackage().project?.name || NO_VALUE }
  ]);

  readonly modelElevationSelectOptions = computed<DropdownData[]>(() => [
    { id: 'modelElevation', firstLabel: this.copySourceSummary() || NO_VALUE }
  ]);

  readonly discountPercentage = computed(() => this.sourcePackage().discountPercentage ?? 0);

  readonly optionRows = computed(() => {
    const discount = this.discountPercentage();
    return (this.sourcePackage().products ?? []).map((entry: DsmPackageProductSummary) => {
      const product = entry.product ?? null;
      const subcategory = product?.subcategory ?? null;
      const unitPrice = entry.unitPrice ?? 0;
      return {
        id: entry.id,
        code: product?.code,
        unitPrice,
        discountedUnitPrice: this.#packageService.amountAfterDiscount(unitPrice, discount),
        description: product?.description,
        category: subcategory?.category?.code,
        subcategory: subcategory?.code,
        type: entry.productType?.name ?? product?.productClass?.name,
        uom: entry.uofm?.code ?? product?.uofm?.code,
        count: entry.count || 1
      };
    });
  });

  constructor() {
    effect(() => {
      if (!this.visible()) return;

      this.#selectedIds.set(new Set());
      this.#expandOverrides.set(new Map());
      this.searchTerm.set('');
      this.outcomes.set(null);
      this.copyError.set(null);
    });
  }

  toggleExpand(row: ModelRow): void {
    this.#expandOverrides.update(overrides => new Map(overrides).set(row.modelId, !row.expanded));
  }

  toggleElevation(row: ElevationRow): void {
    if (!row.selectable) return;

    this.#selectedIds.update(ids => {
      const next = new Set(ids);
      if (next.has(row.elevationId)) next.delete(row.elevationId);
      else next.add(row.elevationId);

      return next;
    });
  }

  toggleModel(row: ModelRow): void {
    const selectableIds = row.elevations
      .filter(elevation => elevation.selectable)
      .map(elevation => elevation.elevationId);
    if (!selectableIds.length) return;

    const allSelected = row.allSelected;
    this.#selectedIds.update(ids => {
      const next = new Set(ids);
      selectableIds.forEach(id => (allSelected ? next.delete(id) : next.add(id)));

      return next;
    });
  }

  toggleSelectAll(): void {
    const allIds = this.#allSelectableIds();
    if (!allIds.length) return;

    this.#selectedIds.set(this.allSelected() ? new Set() : new Set(allIds));
  }

  retryTargets(): void {
    this.#modelElevationsResource.reload();
  }

  retryPreview(): void {
    this.#previewResource.reload();
  }

  close(): void {
    this.visible.set(false);
  }

  copy(): void {
    const packageId = this.#packageId();
    if (!this.canCopy()) return;

    const selectedRows = this.#allModelRows().flatMap(model =>
      model.elevations
        .filter(elevation => elevation.selected)
        .map(elevation => ({
          modelId: elevation.modelId,
          elevationId: elevation.elevationId,
          label: `${model.modelName} / ${elevation.label}`
        }))
    );

    if (!selectedRows.length) return;

    const preview = this.#previewByElevationId();
    const labels = new Map(selectedRows.map(row => [row.elevationId, row.label]));

    // The preview already told us which selected targets are refused — those are never
    // sent, matching "excluded from the bulk create payload" (the backend itself still
    // independently re-validates whatever is sent).
    const refusedUpfront = selectedRows.filter(row => preview.get(row.elevationId)?.canCreate === false);
    const toSend = selectedRows.filter(row => preview.get(row.elevationId)?.canCreate !== false);

    if (!toSend.length) {
      this.outcomes.set(
        refusedUpfront.map(row => ({
          label: row.label,
          status: 'refused' as const,
          reason: preview.get(row.elevationId)?.reason ?? 'Cannot copy to this target.'
        }))
      );

      return;
    }

    this.isCopying.set(true);
    this.copyError.set(null);
    this.outcomes.set(null);

    this.#packageService
      .copyPackage(
        packageId,
        toSend.map(({ modelId, elevationId }) => ({ modelId, elevationId }))
      )
      .pipe(takeUntilDestroyed(this.#destroyRef))
      .subscribe({
        next: results => {
          this.isCopying.set(false);
          this.#handleResults(results, refusedUpfront, labels, preview);
        },
        error: () => {
          this.isCopying.set(false);
          this.copyError.set('Something went wrong while copying this package.');
        }
      });
  }

  /**
   * A copy is not all-or-nothing: the backend creates what it can and skips the rest.
   * When everything was created (and nothing was refused up front) the dialog just
   * closes; anything else keeps it open so every outcome is read, not toasted away.
   */
  #handleResults(
    results: DsmPackageCopyTargetResult[],
    refusedUpfront: { elevationId: string; label: string }[],
    labels: Map<string, string>,
    preview: Map<string, DsmPackageCopyPreviewTarget>
  ): void {
    const created = results.filter(result => result.status === DsmPackageCopyStatus.Created);
    const notCreated = results.filter(result => result.status !== DsmPackageCopyStatus.Created);

    if (created.length) {
      this.#messageService.add({
        severity: 'success',
        summary: `Package copied to ${created.length} ${created.length === 1 ? 'elevation' : 'elevations'}.`
      });
      this.#packageService.triggerRefresh();
    }

    if (!notCreated.length && !refusedUpfront.length) {
      this.close();

      return;
    }

    this.outcomes.set([
      ...results.map(result => ({
        label: labels.get(result.elevationId) ?? NO_VALUE,
        status: (result.status === DsmPackageCopyStatus.Created ? 'created' : 'skipped') as CopyOutcome['status'],
        reason: result.reason ?? null
      })),
      ...refusedUpfront.map(row => ({
        label: row.label,
        status: 'refused' as const,
        reason: preview.get(row.elevationId)?.reason ?? null
      }))
    ]);

    // Whatever this copy touched needs a fresh eligibility answer — a created target now
    // holds a package with this name, and a skip may carry a different reason than the pre-check found.
    this.#selectedIds.update(ids => {
      const next = new Set(ids);
      results.forEach(result => next.delete(result.elevationId));
      refusedUpfront.forEach(row => next.delete(row.elevationId));

      return next;
    });
  }

  #isSourcePair(pair: DsmPackageProjectModelElevationOption): boolean {
    const source = this.sourcePackage();
    return pair.modelId === source.modelId && pair.elevationId === source.elevationId;
  }

  /**
   * Pure — takes every input as a param so building every row stays a straight function.
   * Eligibility comes from the upfront batch preview, not from whether the row happens
   * to be selected: a target already known refused is disabled outright. Other packages on
   * a model/elevation do not refuse it; only one with the source's name does.
   */
  #buildElevationRow(
    pair: DsmPackageProjectModelElevationOption,
    term: string,
    selected: Set<string>,
    preview: Map<string, DsmPackageCopyPreviewTarget>,
    checking: boolean,
    failed: boolean,
    totalOptions: number
  ): ElevationRow {
    const elevationId = pair.elevationId;
    const isSource = this.#isSourcePair(pair);
    const check = preview.get(elevationId);

    let state: RowState = null;
    let reason: string | null = null;
    let missingOptionsTooltip: string | null = null;
    let selectable = false;

    if (isSource) {
      reason = 'Source package';
    } else if (check) {
      if (!check.canCreate) {
        state = 'refused';
        reason = check.reason || 'Cannot copy to this target.';
      } else {
        selectable = true;
        const available = check.availableProductCount ?? totalOptions;
        if (totalOptions > 0 && available < totalOptions) {
          state = 'partial';
          reason = `${available} of ${totalOptions} options available`;
          missingOptionsTooltip = (check.products ?? [])
            .filter(p => !p.isAvailable)
            .map(p => `${p.code || p.description || 'Option'}: ${p.reason || 'not configured'}`)
            .join('; ');
        } else {
          state = 'ok';
        }
      }
    } else if (failed) {
      state = 'failed';
      reason = 'Could not check this target';
    } else if (checking) {
      state = 'checking';
      reason = 'Checking…';
    }

    const code = pair.elevationCode || NO_VALUE;

    return {
      elevationId,
      modelId: pair.modelId,
      label: `Elevation ${code}`,
      matchesSearch: !term || code.toLowerCase().includes(term),
      isSource,
      selectable,
      selected: selectable && selected.has(elevationId),
      state,
      reason,
      missingOptionsTooltip: missingOptionsTooltip || null
    };
  }

  #buildModelRow(
    modelId: string,
    modelName: string,
    pairs: DsmPackageProjectModelElevationOption[],
    source: DsmPackageDetail,
    term: string,
    overrides: Map<string, boolean>,
    selected: Set<string>,
    preview: Map<string, DsmPackageCopyPreviewTarget>,
    checking: boolean,
    failed: boolean,
    totalOptions: number,
    totalModelCount: number
  ): ModelRow {
    const modelNameMatches = !term || modelName.toLowerCase().includes(term);
    const elevations = pairs
      .slice()
      .sort((a, b) => (a.elevationCode || '').localeCompare(b.elevationCode || '', undefined, { numeric: true }))
      .map(pair => this.#buildElevationRow(pair, term, selected, preview, checking, failed, totalOptions));

    const anyElevationMatches = !term || elevations.some(elevation => elevation.matchesSearch);
    const visible = modelNameMatches || anyElevationMatches;
    // Collapsed by default past 3 models; the source model always starts open; search
    // always opens whatever it matches. A manual toggle is remembered from then on.
    const defaultExpanded = totalModelCount <= 3 || modelId === source.modelId;
    const expanded = term ? true : overrides.has(modelId) ? !!overrides.get(modelId) : defaultExpanded;

    const selectableElevations = elevations.filter(elevation => elevation.selectable);
    const selectedElevations = selectableElevations.filter(elevation => elevation.selected);

    return {
      modelId,
      modelName,
      expanded,
      visible,
      elevations,
      visibleElevations: modelNameMatches ? elevations : elevations.filter(elevation => elevation.matchesSearch),
      selectableCount: selectableElevations.length,
      selectedCount: selectedElevations.length,
      allSelected: selectableElevations.length > 0 && selectedElevations.length === selectableElevations.length
    };
  }
}
